import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CalendarDays, Clock, MapPin, CheckCircle2, AlertCircle, Loader2, Search,
  Ticket, DoorOpen, UserCheck, Map as MapIcon, FileText, Share2, Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(d) {
  if (!d) return "";
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "long", month: "long", day: "numeric", year: "numeric",
    });
  } catch { return d; }
}

function formatTime(t) {
  if (!t) return "";
  try {
    const [h, m] = t.split(":");
    const d = new Date(); d.setHours(+h, +m, 0, 0);
    return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch { return t; }
}

/**
 * Mobile-optimized check-in for global events. The learner can ONLY search
 * the roster — no manual entry / walk-in here. After check-in, the page
 * surfaces their passport itinerary.
 */
export default function EventCheckInMobile() {
  const { eventId, learnerId, attendeeId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loadingEvent, setLoadingEvent] = useState(true);
  // 'Find your name' typeahead against the event's attendee list.
  const [search, setSearch] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checkedIn, setCheckedIn] = useState(null);
  const [shareState, setShareState] = useState("idle"); // idle|copied|sharing

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/global-events/${eventId}`);
        setEvent(data);
      } catch { setEvent({ error: true }); }
      finally { setLoadingEvent(false); }
    })();
  }, [eventId]);

  // Persistent itinerary URL — legacy learner-based (/event-checkin/{eventId}/me/{learnerId}).
  useEffect(() => {
    if (!learnerId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/global-events/${eventId}/itinerary/${learnerId}`);
        setCheckedIn({
          first_name: data?.learner?.first_name || "",
          last_name: data?.learner?.last_name || "",
          duplicate: true,
          passport_name: data?.passport_name || "",
          map_url: data?.map_url || "",
          map_filename: data?.map_filename || "",
          itinerary: data?.itinerary || [],
          persistent: true,
          learner_id: learnerId,
        });
      } catch (err) {
        setCheckedIn({ error: err?.response?.status === 404 ? "not_on_roster" : "error" });
      }
    })();
  }, [eventId, learnerId]);

  // Persistent itinerary URL — new attendee-based (/event-checkin/{eventId}/attendee/{attendeeId}).
  useEffect(() => {
    if (!attendeeId) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/public/global-events/${eventId}/attendee-itinerary/${attendeeId}`);
        setCheckedIn({
          first_name: data?.attendee?.first_name || "",
          last_name: data?.attendee?.last_name || "",
          duplicate: true,
          passport_name: data?.passport_name || "",
          map_url: data?.event?.map_url || "",
          map_filename: data?.event?.map_filename || "",
          itinerary: data?.itinerary || [],
          persistent: true,
          attendee_id: attendeeId,
        });
      } catch (err) {
        setCheckedIn({ error: err?.response?.status === 404 ? "not_on_roster" : "error" });
      }
    })();
  }, [eventId, attendeeId]);

  const shareUrl = checkedIn?.attendee_id
    ? `${window.location.origin}/event-checkin/${eventId}/attendee/${checkedIn.attendee_id}`
    : checkedIn?.learner_id
    ? `${window.location.origin}/event-checkin/${eventId}/me/${checkedIn.learner_id}`
    : (attendeeId ? `${window.location.origin}/event-checkin/${eventId}/attendee/${attendeeId}`
      : learnerId ? `${window.location.origin}/event-checkin/${eventId}/me/${learnerId}` : "");

  const handleShare = async () => {
    if (!shareUrl) return;
    const title = event?.name ? `My itinerary — ${event.name}` : "My itinerary";
    const text = `${title}`;
    setShareState("sharing");
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url: shareUrl });
        setShareState("idle");
        return;
      }
    } catch {
      // user cancel — fall through to copy fallback
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareState("copied");
      setTimeout(() => setShareState("idle"), 1800);
    } catch {
      setShareState("idle");
    }
  };

  const doSearch = useCallback(async (q) => {
    const query = (q ?? "").trim();
    if (query.length < 2) { setMatches([]); setHasSearched(false); return; }
    setSearching(true);
    try {
      const { data } = await axios.get(
        `${API}/public/global-events/${eventId}/attendee-search`,
        { params: { q: query } }
      );
      setMatches(Array.isArray(data) ? data : []);
      setHasSearched(true);
    } finally { setSearching(false); }
  }, [eventId]);

  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 250);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  const checkInAttendee = useCallback(async (attendee) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/public/global-events/${eventId}/attendee-checkin`,
        { attendee_id: attendee.id }
      );
      const aid = data?.attendee?.id || attendee.id;
      // Persistent URL — bookmarkable, shareable, survives refresh.
      navigate(`/event-checkin/${eventId}/attendee/${aid}`, { replace: true });
      setCheckedIn({
        first_name: data?.attendee?.first_name || attendee.first_name,
        last_name: data?.attendee?.last_name || attendee.last_name,
        duplicate: !!data?.duplicate,
        passport_name: data?.passport_name || "",
        map_url: data?.map_url || "",
        map_filename: data?.map_filename || "",
        itinerary: data?.itinerary || [],
        persistent: false,
        attendee_id: aid,
      });
    } catch (err) {
      setCheckedIn({ error: err?.response?.status === 404 ? "not_on_roster" : "error" });
    } finally {
      setSubmitting(false);
    }
  }, [eventId, navigate]);

  if (loadingEvent) {
    return (
      <Shell>
        <Card>
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
        </Card>
      </Shell>
    );
  }

  // Persistent URL is hydrating — show a spinner instead of the search UI
  // so the user never sees a flash of the wrong screen.
  if ((learnerId || attendeeId) && !checkedIn) {
    return (
      <Shell>
        <Card>
          <div className="flex justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--primary-color, #4A90D9)" }} />
          </div>
        </Card>
      </Shell>
    );
  }

  if (!event || event.error) {
    return (
      <Shell>
        <Card>
          <div className="text-center space-y-3 py-6">
            <AlertCircle className="h-10 w-10 mx-auto text-red-500" />
            <h2 className="font-heading text-xl font-bold text-gray-900">Event not found</h2>
            <p className="text-sm text-gray-500 font-body">This check-in link is invalid or has been removed.</p>
          </div>
        </Card>
      </Shell>
    );
  }

  // Confirmation + itinerary
  if (checkedIn && !checkedIn.error) {
    return (
      <Shell>
        <Card testid="checkin-confirm">
          <div className="text-center space-y-3 py-4">
            <div
              className="inline-flex items-center justify-center h-14 w-14 rounded-full"
              style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 15%, white)" }}
            >
              <CheckCircle2 className="h-8 w-8" style={{ color: "var(--primary-color, #4A90D9)" }} />
            </div>
            <h2 className="font-heading text-2xl font-bold text-gray-900" data-testid="checkin-confirm-heading">
              {checkedIn.persistent
                ? "Your itinerary"
                : checkedIn.duplicate
                ? "You're already checked in"
                : "You're checked in"}
            </h2>
            <p className="text-base font-body text-gray-700">{checkedIn.first_name} {checkedIn.last_name}</p>
            {checkedIn.passport_name ? (
              <p className="text-xs font-body text-gray-500 inline-flex items-center justify-center gap-1.5">
                <Ticket className="h-3.5 w-3.5" /> {checkedIn.passport_name}
              </p>
            ) : null}
            {shareUrl ? (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleShare}
                  disabled={shareState === "sharing"}
                  data-testid="itinerary-share-btn"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-body font-semibold transition-colors disabled:opacity-60"
                  style={{
                    backgroundColor: shareState === "copied"
                      ? "color-mix(in srgb, var(--primary-color, #4A90D9) 18%, white)"
                      : "var(--primary-color, #4A90D9)",
                    color: shareState === "copied" ? "var(--primary-color, #4A90D9)" : "white",
                  }}
                >
                  {shareState === "copied" ? (
                    <><Check className="h-4 w-4" /> Link copied</>
                  ) : shareState === "sharing" ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Sharing…</>
                  ) : (
                    <><Share2 className="h-4 w-4" /> Save / Share itinerary</>
                  )}
                </button>
                <p className="text-[11px] font-body text-gray-500 mt-2 px-4">
                  Bookmark or share this link to pull up your itinerary anytime.
                </p>
              </div>
            ) : null}
          </div>
        </Card>

        <Card testid="checkin-itinerary">
          <div className="flex items-center gap-2 mb-4">
            <span
              className="text-[10px] font-body font-semibold uppercase tracking-[0.15em] px-2 py-1 rounded"
              style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}
            >
              Your itinerary
            </span>
          </div>
          {checkedIn.map_url ? (
            <a
              href={checkedIn.map_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${checkedIn.map_url}` : checkedIn.map_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mb-4 flex items-center justify-between gap-3 px-4 py-3 rounded-xl border bg-white active:bg-gray-50 transition-colors"
              style={{ borderColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 30%, white)" }}
              data-testid="checkin-map-link"
            >
              <div className="min-w-0 flex items-center gap-3">
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}
                >
                  <MapIcon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-body font-semibold text-gray-900">Venue map</p>
                  <p className="text-xs font-body text-gray-500 truncate inline-flex items-center gap-1.5">
                    <FileText className="h-3 w-3" /> {checkedIn.map_filename || "View map"}
                  </p>
                </div>
              </div>
              <span
                className="text-xs font-body font-semibold shrink-0"
                style={{ color: "var(--primary-color, #4A90D9)" }}
              >
                Open
              </span>
            </a>
          ) : null}
          {checkedIn.itinerary.length === 0 ? (
            <p className="text-sm font-body text-gray-500 py-6 text-center">
              No sessions assigned to your passport yet.
            </p>
          ) : (
            <ol className="space-y-3" data-testid="checkin-itinerary-list">
              {checkedIn.itinerary.map((s, idx) => (
                <li
                  key={s.session_id}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-3.5"
                  data-testid={`itinerary-session-${s.session_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-body font-bold"
                      style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}
                    >
                      {idx + 1}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading text-base font-semibold text-gray-900 leading-snug">{s.name}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600 font-body">
                        <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {formatTime(s.start_time)} – {formatTime(s.end_time)}</span>
                        {s.room ? (
                          <span className="inline-flex items-center gap-1.5"><DoorOpen className="h-3.5 w-3.5" /> {s.room}</span>
                        ) : null}
                        {s.session_location ? (
                          <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {s.session_location}</span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </Shell>
    );
  }

  // Error states (not on the attendee list / generic)
  if (checkedIn?.error === "not_on_roster") {
    return (
      <Shell>
        <Card>
          <div className="text-center space-y-3 py-6">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-amber-100">
              <AlertCircle className="h-8 w-8 text-amber-700" />
            </div>
            <h2 className="font-heading text-xl font-bold text-gray-900">Not on the attendee list</h2>
            <p className="text-sm font-body text-gray-600 max-w-sm mx-auto">
              We couldn&apos;t find your name on this event&apos;s attendee list. Please find an organizer to be added.
            </p>
            <button
              onClick={() => { setCheckedIn(null); setSearch(""); navigate(`/event-checkin/${eventId}`, { replace: true }); }}
              data-testid="checkin-back-search"
              className="text-xs font-body underline text-gray-500 hover:text-gray-700 pt-2"
            >
              Try a different search
            </button>
          </div>
        </Card>
      </Shell>
    );
  }

  // Default: event header + name-entry form
  return (
    <Shell>
      <Card testid="checkin-event-card">
        <div className="flex items-center gap-2 mb-3">
          <span
            className="text-[10px] font-body font-semibold uppercase tracking-[0.15em] px-2 py-1 rounded"
            style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}
          >
            Check-in
          </span>
        </div>
        <h1 className="font-heading text-xl sm:text-2xl font-bold leading-tight text-gray-900" data-testid="checkin-event-name">{event.name}</h1>
        <div className="space-y-1.5 text-sm text-gray-600 mt-3">
          <Detail icon={CalendarDays} value={formatDate(event.date)} />
          <Detail icon={Clock} value={formatTime(event.start_time)} />
          {event.location && <Detail icon={MapPin} value={event.location} />}
        </div>
      </Card>

      <Card testid="checkin-search-card">
        <label className="block">
          <span className="text-xs font-body font-semibold uppercase tracking-wider text-gray-500">Find your name</span>
          <div className="relative mt-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Start typing your first or last name…"
              data-testid="checkin-search-input"
              className="w-full h-12 pl-10 pr-3 rounded-xl border border-gray-300 bg-white text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2"
              style={{ borderColor: search ? "var(--primary-color, #4A90D9)" : undefined }}
            />
          </div>
        </label>

        <div className="mt-4">
          {!search.trim() ? (
            <p className="text-sm font-body text-gray-500 text-center py-6">
              Type at least 2 letters of your name to search.
            </p>
          ) : searching ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
            </div>
          ) : hasSearched && matches.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-4 text-center" data-testid="checkin-no-matches">
              <AlertCircle className="h-5 w-5 text-amber-700 mx-auto mb-1.5" />
              <p className="text-sm font-body text-amber-900 font-semibold">No match found</p>
              <p className="text-xs font-body text-amber-800 mt-1 max-w-sm mx-auto">
                If you&apos;re attending this event, please find an organizer to be added to the list.
              </p>
            </div>
          ) : (
            <ul className="space-y-2" data-testid="checkin-matches">
              {matches.map((a) => (
                <li key={a.id}>
                  <button
                    type="button"
                    onClick={() => checkInAttendee(a)}
                    disabled={submitting}
                    data-testid={`checkin-match-${a.id}`}
                    className="w-full flex items-center justify-between gap-3 text-left px-4 py-3.5 rounded-xl border border-gray-200 bg-white hover:border-gray-300 active:bg-gray-50 transition-colors disabled:opacity-60"
                  >
                    <div className="min-w-0 flex items-center gap-3">
                      <div
                        className="h-9 w-9 rounded-full flex items-center justify-center text-xs font-body font-semibold shrink-0"
                        style={{ backgroundColor: "color-mix(in srgb, var(--primary-color, #4A90D9) 12%, white)", color: "var(--primary-color, #4A90D9)" }}
                      >
                        {(a.first_name || "?")[0]}{(a.last_name || "?")[0]}
                      </div>
                      <span className="text-base font-body font-medium text-gray-900 truncate">
                        {a.first_name} {a.last_name}
                      </span>
                    </div>
                    <UserCheck className="h-4 w-4 text-gray-400 shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-[100dvh] bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-6 sm:py-10 pb-[20vh]">
      <div className="max-w-md mx-auto space-y-4">{children}</div>
    </div>
  );
}

function Card({ children, testid }) {
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-gray-200 px-5 py-5 sm:px-6 sm:py-6" data-testid={testid}>
      {children}
    </div>
  );
}

function Detail({ icon: Icon, value }) {
  return (
    <p className="inline-flex items-center gap-2"><Icon className="h-3.5 w-3.5 text-gray-400 shrink-0" /> <span>{value}</span></p>
  );
}
